import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { fetchOKRDashboard } from '@/store/slices/okrsSlice';
import { GlassCard, GlassBadge, GlassButton } from '@/design-system';
import { formatPercent } from '@/utils/formatters';
import {
  Target,
  TrendingUp,
  BarChart3,
  PieChart,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Building2,
  Layers,
  FileCode,
  Award,
} from 'lucide-react';
import type { OKRDashboardData, OKRLevel, OKRStatusNew } from '@/types';

interface OKRDashboardProps {
  fiscalYear?: number;
  quarter?: number;
  onClose: () => void;
}

const OKRDashboard: React.FC<OKRDashboardProps> = ({
  fiscalYear,
  quarter,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { dashboard, isLoadingDashboard, error } = useSelector((state: RootState) => state.okrs);

  useEffect(() => {
    dispatch(fetchOKRDashboard({ fiscalYear, quarter }));
  }, [dispatch, fiscalYear, quarter]);

  const getLevelIcon = (level: OKRLevel) => {
    switch (level) {
      case 'IS':
        return <Layers className="w-5 h-5" />;
      case 'PROGRAM':
        return <Building2 className="w-5 h-5" />;
      case 'EPIC':
        return <FileCode className="w-5 h-5" />;
    }
  };

  const getLevelColor = (level: OKRLevel) => {
    switch (level) {
      case 'IS':
        return 'purple';
      case 'PROGRAM':
        return 'blue';
      case 'EPIC':
        return 'green';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-status-success';
      case 'COMPLETED':
        return 'bg-status-info';
      case 'DRAFT':
        return 'bg-status-warning';
      case 'CANCELLED':
        return 'bg-status-error';
      default:
        return 'bg-muted';
    }
  };

  const getProgressColor = (progress: number | null) => {
    if (progress === null) return 'bg-muted';
    if (progress >= 75) return 'bg-status-success';
    if (progress >= 50) return 'bg-status-warning';
    if (progress >= 25) return 'bg-chart-orange';
    return 'bg-status-error';
  };

  if (isLoadingDashboard) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-accent-blue animate-spin" />
        <span className="ml-3 text-muted-foreground">Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-status-error mx-auto mb-4" />
        <p className="text-status-error">{error}</p>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-8 text-center">
        <BarChart3 className="w-12 h-12 text-muted mx-auto mb-4" />
        <p className="text-muted-foreground">No dashboard data available</p>
      </div>
    );
  }

  const { summary, byLevel, byQuarter, alignment, topObjectives, atRisk } = dashboard;

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Objectives</p>
              <p className="text-2xl font-bold text-foreground mt-1">{summary.totalObjectives}</p>
            </div>
            <div className="p-3 rounded-lg bg-accent-blue-bg">
              <Target className="w-6 h-6 text-accent-blue" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Key Results</p>
              <p className="text-2xl font-bold text-foreground mt-1">{summary.totalKeyResults}</p>
            </div>
            <div className="p-3 rounded-lg bg-status-success-bg">
              <TrendingUp className="w-6 h-6 text-status-success" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Progress</p>
              <p className="text-2xl font-bold text-foreground mt-1">
                {summary.avgOverallProgress !== null ? formatPercent(summary.avgOverallProgress, { decimals: 0 }) : 'N/A'}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-accent-blue-bg">
              <PieChart className="w-6 h-6 text-accent-blue" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Completion Rate</p>
              <p className="text-2xl font-bold text-foreground mt-1">{formatPercent(summary.completionRate, { decimals: 0 })}</p>
            </div>
            <div className="p-3 rounded-lg bg-accent-cyan-soft">
              <CheckCircle className="w-6 h-6 text-accent-cyan" />
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Status Distribution */}
      <GlassCard className="p-4">
        <h3 className="text-sm font-medium text-muted-foreground mb-4">Status Distribution</h3>
        <div className="flex gap-4">
          {Object.entries(summary.byStatus).map(([status, count]) => (
            <div key={status} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`} />
              <span className="text-sm text-muted-foreground">{status}</span>
              <span className="text-sm font-medium text-foreground">{count}</span>
            </div>
          ))}
        </div>
      </GlassCard>

      {/* By Level */}
      <div className="grid grid-cols-3 gap-4">
        {byLevel.map((levelData) => (
          <GlassCard key={levelData.level} className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg bg-${getLevelColor(levelData.level)}-500/20`}>
                {getLevelIcon(levelData.level)}
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">{levelData.level} Level</p>
                <p className="text-xs text-muted-foreground">{levelData.totalObjectives} objectives</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Progress</span>
                <span className="text-sm font-medium text-foreground">
                  {levelData.avgProgress !== null ? formatPercent(levelData.avgProgress, { decimals: 0 }) : 'N/A'}
                </span>
              </div>
              <div className="w-full h-2 bg-surface-muted rounded-full overflow-hidden">
                <div
                  className={`h-full ${getProgressColor(levelData.avgProgress)} transition-all duration-300`}
                  style={{ width: `${Math.min(levelData.avgProgress ?? 0, 100)}%` }}
                />
              </div>

              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Key Results</span>
                <span className="text-sm text-foreground">{levelData.keyResultsCount}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Completion</span>
                <span className="text-sm text-foreground">{formatPercent(levelData.completionRate, { decimals: 0 })}</span>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Alignment Metrics */}
      <GlassCard className="p-4">
        <h3 className="text-sm font-medium text-muted-foreground mb-4">Alignment Overview</h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-foreground">{alignment.totalAligned}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Aligned</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-accent-blue">{alignment.alignedToIS}</p>
            <p className="text-xs text-muted-foreground mt-1">To IS</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-accent-blue">{alignment.alignedToProgram}</p>
            <p className="text-xs text-muted-foreground mt-1">To Program</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-status-warning">{alignment.orphanObjectives}</p>
            <p className="text-xs text-muted-foreground mt-1">Unaligned</p>
          </div>
        </div>
      </GlassCard>

      <div className="grid grid-cols-2 gap-4">
        {/* Top Performers */}
        <GlassCard className="p-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
            <Award className="w-4 h-4 text-status-success" />
            Top Performing Objectives
          </h3>
          {topObjectives.length === 0 ? (
            <p className="text-sm text-muted text-center py-4">No data available</p>
          ) : (
            <div className="space-y-3">
              {topObjectives.slice(0, 5).map((obj) => (
                <div key={obj.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0 flex-1 mr-3">
                    <div className={`p-1 rounded bg-${getLevelColor(obj.level)}-500/20`}>
                      {getLevelIcon(obj.level)}
                    </div>
                    <span className="text-sm text-foreground truncate">{obj.title}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-12 h-1.5 bg-surface-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full ${getProgressColor(obj.progress)} transition-all duration-300`}
                        style={{ width: `${Math.min(obj.progress ?? 0, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground w-10 text-right">
                      {formatPercent(obj.progress ?? 0, { decimals: 0 })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>

        {/* At Risk */}
        <GlassCard className="p-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-status-error" />
            At Risk Objectives
          </h3>
          {atRisk.length === 0 ? (
            <p className="text-sm text-muted text-center py-4">No at-risk objectives</p>
          ) : (
            <div className="space-y-3">
              {atRisk.slice(0, 5).map((obj) => (
                <div key={obj.id} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0 flex-1 mr-3">
                    <div className={`p-1 rounded bg-${getLevelColor(obj.level)}-500/20`}>
                      {getLevelIcon(obj.level)}
                    </div>
                    <span className="text-sm text-foreground truncate">{obj.title}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-status-error">
                      {formatPercent(obj.progress ?? 0, { decimals: 0 })}
                    </span>
                    <p className="text-xs text-muted">
                      Expected: {formatPercent(obj.expectedProgress, { decimals: 0 })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </GlassCard>
      </div>

      {/* Quarterly Breakdown */}
      {byQuarter.length > 0 && (
        <GlassCard className="p-4">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">Quarterly Breakdown</h3>
          <div className="grid grid-cols-4 gap-4">
            {byQuarter.map((q) => (
              <div key={`${q.fiscalYear}-${q.quarter}`} className="text-center p-3 bg-surface-muted/50 rounded-lg">
                <p className="text-sm font-medium text-foreground">
                  Q{q.quarter} FY{q.fiscalYear.toString().slice(-2)}
                </p>
                <p className="text-2xl font-bold text-foreground mt-2">{q.totalObjectives}</p>
                <p className="text-xs text-muted-foreground">objectives</p>
                <div className="mt-2">
                  <div className="w-full h-1.5 bg-surface-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getProgressColor(q.avgProgress)} transition-all duration-300`}
                      style={{ width: `${Math.min(q.avgProgress ?? 0, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {q.avgProgress !== null ? `${formatPercent(q.avgProgress, { decimals: 0 })} avg` : 'N/A'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default OKRDashboard;
